// Exportacion de reportes sin librerias externas.
//
//  - Imprimir y PDF pasan por window.print(): el CSS de impresion de index.html
//    deja solo el area del reporte. En el PDF el usuario elige "Guardar como
//    PDF" en el dialogo del navegador, que sale con el nombre ya puesto.
//  - Excel se genera como texto separado por tabulaciones con BOM UTF-8, que es
//    lo que Excel espera para abrirlo sin avisos.
//  - Word se genera como HTML con extension .doc, que Word abre nativamente y
//    conserva la tabla con sus anchos.

// Descarga un texto como archivo. El BOM hace que Excel y Word reconozcan
// UTF-8 y no zeigen los acentos rotos.
const descargar = (contenido, nombreArchivo, tipoMime) => {
    const blob = new Blob(["\ufeff" + contenido], { type: tipoMime });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement("a");

    enlace.href = url;
    enlace.download = nombreArchivo;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);

    // Se libera despues de un tick: si se revoca de inmediato, el navegador
    // todavia no alcanzo a leer el archivo.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Las celdas que no son texto se pasan por un formateador; si no, se limpian
// los saltos de linea que rompen el formato tabular.
const celda = (valor) => {
    if (valor === null || valor === undefined) return "";

    return String(valor).replace(/[\t\r\n]+/g, " ").trim();
}

const marcasDeTiempo = () => {
    const ahora = new Date();
    const dos = (n) => String(n).padStart(2, "0");

    return ahora.getFullYear()
        + dos(ahora.getMonth() + 1)
        + dos(ahora.getDate()) + "-"
        + dos(ahora.getHours()) + dos(ahora.getMinutes());
}

const nombreArchivo = (titulo) => (titulo || "reporte")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

// Excel: columnas separadas por tabulador, filas por salto de linea. Los
// valores que empiezan con =, +, - o @ los lleva Word/Excel como formula, asi
// que se antepone un apostrofo para que se muestren como texto.
const paraExcel = (valor) => {
    const texto = celda(valor);

    return /^[=+\-@]/.test(texto) ? "'" + texto : texto;
}

export const exportarExcel = ({ titulo, subtitulo, indicadores, columnas, filas }) => {
    const lineas = [];

    if (titulo) lineas.push(paraExcel(titulo));
    if (subtitulo) lineas.push(paraExcel(subtitulo));

    lineas.push("Generado: " + paraExcel(new Date().toLocaleString("es-PE")));

    if (indicadores && indicadores.length) {
        lineas.push("");
        lineas.push(indicadores.map((i) => paraExcel(i.etiqueta)).join("\t"));
        lineas.push(indicadores.map((i) => paraExcel(i.valor)).join("\t"));
    }

    lineas.push("");
    lineas.push(columnas.map((c) => paraExcel(c.titulo)).join("\t"));

    for (const fila of filas) {
        lineas.push(columnas.map((c) => paraExcel(fila[c.campo])).join("\t"));
    }

    descargar(lineas.join("\r\n"), nombreArchivo(titulo) + "-" + marcasDeTiempo() + ".xls",
        "application/vnd.ms-excel;charset=utf-8");
}

// Word: tabla HTML con los anchos en porcentaje, que es lo que Word respeta.
const htmlTabla = (columnas, filas) => {
    const encabezado = columnas.map((c) =>
        `<th style="width:${c.ancho || 12}%; border:1px solid #999; padding:4px; background:#5b2d8e; color:#fff;">`
        + celda(c.titulo) + "</th>").join("");

    const cuerpo = filas.map((fila) => "<tr>"
        + columnas.map((c) =>
            `<td style="border:1px solid #999; padding:4px;">` + celda(fila[c.campo]) + "</td>").join("")
        + "</tr>").join("");

    return `<table style="border-collapse:collapse; width:100%; font-family:Calibri,Arial,sans-serif; font-size:10pt;">`
        + `<thead><tr>${encabezado}</tr></thead><tbody>${cuerpo}</tbody></table>`;
}

export const exportarWord = ({ titulo, subtitulo, indicadores, columnas, filas }) => {
    const indicadoresHtml = (indicadores && indicadores.length)
        ? `<table style="width:100%; margin:10pt 0; font-family:Calibri,Arial,sans-serif; font-size:10pt;">`
            + indicadores.map((i) =>
                `<td style="border:1px solid #999; padding:4px;"><b>` + celda(i.etiqueta) + "</b><br/>"
                + celda(i.valor) + "</td>").join("")
            + "</table>"
        : "";

    // Sin logo: al abrir el .doc desde disco la ruta del SVG no resuelve y Word
    // no dibuja SVG, se veria un recuadro roto. El encabezado de texto ya
    // identifica a la institucion.
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8" />
        <title>${celda(titulo)}</title>
        <style>
            body { font-family: Calibri, Arial, sans-serif; font-size: 10pt; }
            h1 { color: #5b2d8e; font-size: 16pt; margin: 0 0 4pt 0; }
            p.sub { color: #555; margin: 0 0 4pt 0; }
        </style></head><body>`
        + `<h1>${celda(titulo)}</h1>`
        + (subtitulo ? `<p class="sub">${celda(subtitulo)}</p>` : "")
        + `<p class="sub">KOLOB - Autismo Peru | Generado: ${celda(new Date().toLocaleString("es-PE"))}</p>`
        + indicadoresHtml
        + htmlTabla(columnas, filas)
        + "</body></html>";

    descargar(html, nombreArchivo(titulo) + "-" + marcasDeTiempo() + ".doc",
        "application/msword;charset=utf-8");
}

// Imprimir y PDF comparten el camino: el CSS de impresion deja solo el area del
// reporte. Para el PDF se renombra el documento un instante, porque el nombre
// que propone el dialogo de impresion es el del title.
const imprimir = (titulo, comoPdf) => {
    const tituloOriginal = document.title;

    if (comoPdf) {
        document.title = nombreArchivo(titulo) + "-" + marcasDeTiempo();
    }

    window.print();

    if (comoPdf) {
        setTimeout(() => { document.title = tituloOriginal; }, 1500);
    }
}

export const imprimirReporte = (titulo) => imprimir(titulo, false);
export const exportarPdf = (titulo) => imprimir(titulo, true);